import LoadingSpinner from "@/components/elements/LoadingSpinner";
import { Suspense } from "react";
import { FormProvider } from "react-hook-form";

import {
  ConnectionBaseFragment,
  ConnectionsDocument,
  ConnectionsWithUsageSummaryDocument,
  DbtConnectionDocument,
  IntegrationAbility,
  IntegrationConfigurationFragment,
  useCreateDbtConnectionMutation,
  useIntegrationConfigurationSuspenseQuery,
} from "@/apollo/types";
import {
  SlideOver,
  SlideOverBody,
  SlideOverCloseButton,
  SlideOverHeader,
} from "@/components/elements/SlideOver";
import Center from "@/components/elements/layout/Center";
import { Overlay } from "@/components/elements/layout/Overlay";
import { useConnections } from "@/features/connectors";
import { IntegrationLogo } from "@/integrations";
import ConnectionForm, {
  ConnectionFormSubmitButton,
  useConnectionForm,
} from "@/pages/Connections/ConnectionForm/ConnectionForm";
import { useToast } from "@/providers/ToastProvider";
import { toBase64 } from "@/utils/encoding";

type DbtSetupSlideOverProps = {
  onConnected?: (connection: ConnectionBaseFragment) => void;
  onError?: (error: Error) => void;
} & Pick<React.ComponentProps<typeof SlideOver>, "show" | "onClose">;
const dbtIntegrationId = "dbt-cloud";

export function DbtSetupSlideOver(props: DbtSetupSlideOverProps) {
  return (
    <SlideOver show={props.show} onClose={props.onClose} bgOverlay>
      <SlideOverCloseButton />
      <SlideOverHeader className="flex items-center">
        <IntegrationLogo id="dbt-cloud" className="mr-2 h-6" /> Set up dbt
      </SlideOverHeader>
      <SlideOverBody className="p-0">
        <Suspense fallback={<LoadingSpinner />}>
          <DbtSetupContainer
            onConnected={props.onConnected}
            onError={props.onError}
          />
        </Suspense>
      </SlideOverBody>
    </SlideOver>
  );
}

function DbtSetupContainer(props: {
  onConnected?: (connection: ConnectionBaseFragment) => void;
  onError?: (error: Error) => void;
}) {
  const toast = useToast();

  const { data } = useIntegrationConfigurationSuspenseQuery({
    variables: {
      id: dbtIntegrationId,
    },
  });
  const integration = data.integrationConfiguration;

  const {
    connections,
    loading: isLoadingConnections,
    refetch: refetchConnections,
  } = useConnections(IntegrationAbility.Utility);

  const dbtConnections = connections.filter(
    (x) => x.integrationId === dbtIntegrationId,
  );
  const hasDbtConnection = dbtConnections.length > 0;

  const [createConnection, { loading }] = useCreateDbtConnectionMutation({
    refetchQueries: [
      ConnectionsDocument,
      DbtConnectionDocument,
      ConnectionsWithUsageSummaryDocument,
    ],
    awaitRefetchQueries: true,
    onCompleted: (data) => {
      refetchConnections();
      props.onConnected?.(data.createDbtConnection);
    },
    onError: (error) => {
      toast("Connection not created", error.message, "error");
      props.onError?.(error);
    },
  });

  return (
    <div className="relative flex h-full flex-col px-6">
      {isLoadingConnections && (
        <Overlay className="absolute bg-white/80">
          <Center className="h-full w-full">
            <LoadingSpinner />
          </Center>
        </Overlay>
      )}
      {hasDbtConnection ? (
        <>dbt connection has been successfully set up</>
      ) : (
        <SetupDbtConnection
          onSubmit={(data) => {
            return createConnection({
              variables: {
                data: toBase64(data),
              },
            });
          }}
          integration={integration}
          isLoading={loading}
        />
      )}
    </div>
  );
}

function SetupDbtConnection(props: {
  integration: IntegrationConfigurationFragment;
  isLoading: boolean;
  onSubmit: (data: any) => void;
}) {
  const formMethods = useConnectionForm(props.integration);
  return (
    <FormProvider {...formMethods}>
      <div className="grow">
        <div className="flex flex-col gap-8">
          <form
            id="integration-form"
            onSubmit={formMethods.handleSubmit(props.onSubmit)}
            className="flex h-full flex-col"
          >
            <ConnectionForm
              integration={props.integration}
              isLoading={props.isLoading}
            />
          </form>
        </div>
      </div>
      <div className="flex shrink-0 grow-0 flex-row-reverse gap-4 py-4">
        <ConnectionFormSubmitButton
          integration={props.integration}
          isLoading={props.isLoading}
        />
      </div>
    </FormProvider>
  );
}
